import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeftIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline'
import { Heading, Text, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import {
  EmptyBlock,
  Field,
  FieldsGrid,
  SectionCard,
} from '../../components/comercial/ClienteContaLayout'
import { clientsService } from '../../services/clients'
import {
  detectIdentificationType,
  formatIdentificationMask,
  identificationTypeLabel,
} from '../../utils/clientIdentification'
import type { Client } from '../../types/clients'
import ClienteContaSaudeTab from './ClienteContaSaudeTab'
import ClienteContaGrupoTab from './ClienteContaGrupoTab'

const MODULE_NAME = 'comercial_clientes'
const EMPTY = '—'

type ActiveTab = 'grupo' | 'cliente' | 'saude'

const tabClass = (activeTab: ActiveTab, tab: ActiveTab) =>
  `py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Erro inesperado ao carregar o cliente.'
}

function formatCityState(client: Client): string {
  const city = client.city?.trim() || ''
  const state = client.state?.trim() || ''
  if (city && state) return `${city} / ${state}`
  if (city) return city
  if (state) return state
  return EMPTY
}

function ClienteTab({
  client,
  nameDisplay,
  identificationLabel,
  identificationDisplay,
  cityStateDisplay,
}: {
  client: Client
  nameDisplay: string
  identificationLabel: string
  identificationDisplay: string
  cityStateDisplay: string
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-3 py-2.5 min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#008B95]/10 dark:bg-teal-900/40">
              <BuildingOffice2Icon className="h-5 w-5 text-[#008B95] dark:text-teal-400" />
            </div>
            <div className="min-w-0">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white break-words">
                {nameDisplay}
              </Heading>
              <Text size="sm" color="muted" className="mt-0.5">
                {identificationLabel} {identificationDisplay}
                {client.id_scgp ? ` · SCGP ${client.id_scgp}` : ''}
                {' · '}
                {cityStateDisplay}
              </Text>
            </div>
          </div>
        </div>

        <SectionCard title="Responsáveis internos">
          <EmptyBlock message="Consultor em breve." />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[30%_45%_1fr] gap-3 items-stretch">
        <div className="flex flex-col gap-3 min-w-0">
          <SectionCard title="Dados da empresa">
            <FieldsGrid>
              <Field label="Razão social" value={nameDisplay} className="sm:col-span-2" />
              <Field label={identificationLabel} value={identificationDisplay} />
              <Field label="Cidade / UF" value={cityStateDisplay} />
              <Field label="Responsável principal" value={EMPTY} />
              <Field label="Telefone" value={EMPTY} />
              <Field label="E-mail" value={EMPTY} className="sm:col-span-2" />
              <Field label="Data de implantação" value={EMPTY} />
              <Field label="Data de renovação" value={EMPTY} />
            </FieldsGrid>
          </SectionCard>

          <SectionCard title="Dados do convênio">
            <FieldsGrid>
              <Field label="Número da apólice" value={EMPTY} />
              <Field label="Valor fatura" value={EMPTY} />
              <Field label="Qtd. vidas" value={EMPTY} />
              <Field label="Plano contratado" value={EMPTY} />
            </FieldsGrid>
          </SectionCard>

          <SectionCard title="Corretor" className="flex-1">
            <FieldsGrid>
              <Field label="Nome" value={EMPTY} className="sm:col-span-2" />
              <Field label="Código" value={EMPTY} />
              <Field label="Telefone" value={EMPTY} />
              <Field label="E-mail" value={EMPTY} className="sm:col-span-2" />
            </FieldsGrid>
          </SectionCard>
        </div>

        <div className="flex flex-col gap-3 min-w-0 min-h-0">
          <SectionCard title="Diário de relacionamento" className="flex-1">
            <EmptyBlock message="Em breve." />
          </SectionCard>
          <SectionCard title="Contatos Wiz com o corretor">
            <EmptyBlock message="Disponível após o vínculo com o corretor." />
          </SectionCard>
        </div>

        <div className="flex flex-col gap-3 min-w-0">
          <SectionCard title="Saúde da conta" className="flex-1">
            <EmptyBlock message="Em breve." />
          </SectionCard>
          <SectionCard title="Próxima ação" className="flex-1">
            <EmptyBlock message="Em breve." />
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

const ComercialClienteConta: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('cliente')
  const [client, setClient] = React.useState<Client | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [notFound, setNotFound] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!id?.trim()) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setLoading(true)
      setPageError(null)
      setNotFound(false)
      try {
        const data = await clientsService.getById(id)
        if (cancelled) return
        if (!data) {
          setNotFound(true)
          setClient(null)
        } else {
          setClient(data)
        }
      } catch (error) {
        if (cancelled) return
        setPageError(getErrorMessage(error))
        setClient(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [id])

  const identificationType = client?.identification
    ? detectIdentificationType(client.identification)
    : 'unknown'
  const identificationLabel = identificationTypeLabel(identificationType)
  const identificationDisplay = client?.identification
    ? formatIdentificationMask(client.identification)
    : EMPTY
  const cityStateDisplay = client ? formatCityState(client) : EMPTY
  const nameDisplay = client?.name?.trim() || EMPTY

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        fallback={
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar esta página.
              </Text>
            </div>
          </div>
        }
      >
        <div className="h-full flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <Link
              to="/comercial/clientes"
              className="inline-flex items-center gap-2 text-gray-900 dark:text-white hover:text-[#008B95] dark:hover:text-teal-400 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 shrink-0" aria-hidden />
              <Heading as="h1" size="2xl" className="text-inherit">
                Conta do cliente
              </Heading>
            </Link>
          </div>

          <div className="px-4 pt-3 border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                type="button"
                onClick={() => setActiveTab('grupo')}
                className={tabClass(activeTab, 'grupo')}
              >
                Grupo
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('cliente')}
                className={tabClass(activeTab, 'cliente')}
              >
                Cliente
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('saude')}
                className={tabClass(activeTab, 'saude')}
              >
                Saúde da conta
              </button>
            </nav>
          </div>

          <div className="p-4 space-y-3">
            {pageError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{pageError}</Text>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : notFound || !client ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                <Text size="sm" color="muted">
                  Cliente não encontrado.
                </Text>
                <Link
                  to="/comercial/clientes"
                  className="mt-2 inline-block text-sm text-[#008B95] hover:underline dark:text-teal-400"
                >
                  Voltar à listagem
                </Link>
              </div>
            ) : (
              <>
                {activeTab === 'grupo' && <ClienteContaGrupoTab />}
                {activeTab === 'cliente' && (
                  <ClienteTab
                    client={client}
                    nameDisplay={nameDisplay}
                    identificationLabel={identificationLabel}
                    identificationDisplay={identificationDisplay}
                    cityStateDisplay={cityStateDisplay}
                  />
                )}
                {activeTab === 'saude' && (
                  <ClienteContaSaudeTab
                    client={client}
                    nameDisplay={nameDisplay}
                    identificationLabel={identificationLabel}
                    identificationDisplay={identificationDisplay}
                    cityStateDisplay={cityStateDisplay}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </PermissionGuard>
    </main>
  )
}

export default ComercialClienteConta
